import type { Game } from '../game/game.js'
import type { Color } from '../primitives/color.js'

/**
 * Wall-clock milliseconds since the epoch.
 *
 * Branded so it cannot be confused with a *duration* in milliseconds, which is
 * the one mix-up in clock code that produces plausible-looking nonsense rather
 * than an obvious crash. Durations stay plain numbers and are always named
 * `...Ms`.
 */
export type Timestamp = number & { readonly __timestamp: unique symbol }

export const timestamp = (value: number): Timestamp => value as Timestamp

export type RoomId = string & { readonly __roomId: unique symbol }
export type PlayerKey = string & { readonly __playerKey: unique symbol }

/**
 * Which of the two seats a player holds. Not a colour — colours alternate every
 * game, seats do not, which is what lets one pair of links outlive a whole
 * evening of rematches.
 */
export type PlayerSlot = 0 | 1

export const OPPONENT: Readonly<Record<PlayerSlot, PlayerSlot>> = { 0: 1, 1: 0 }

/** Slot 0 is White in game 0, Black in game 1, and so on. */
export const colorOfSlot = (slot: PlayerSlot, gameIndex: number): Color =>
  ((slot + gameIndex) % 2) as Color

export const slotOfColor = (color: Color, gameIndex: number): PlayerSlot =>
  ((color + gameIndex) % 2) as PlayerSlot

export type Actor =
  | { readonly kind: 'player'; readonly slot: PlayerSlot }
  | { readonly kind: 'spectator' }

export type TimeControl =
  | { readonly kind: 'unlimited' }
  | { readonly kind: 'clock'; readonly initialMs: number; readonly incrementMs: number }

export interface TimeControlPreset {
  readonly id: string
  readonly label: string
  readonly category: 'bullet' | 'blitz' | 'rapid' | 'unlimited'
  readonly control: TimeControl
}

export const TIME_CONTROLS: readonly TimeControlPreset[] = [
  { id: '1+0', label: '1 min', category: 'bullet', control: { kind: 'clock', initialMs: 60_000, incrementMs: 0 } },
  { id: '3+2', label: '3 | 2', category: 'blitz', control: { kind: 'clock', initialMs: 180_000, incrementMs: 2_000 } },
  { id: '5+0', label: '5 min', category: 'blitz', control: { kind: 'clock', initialMs: 300_000, incrementMs: 0 } },
  { id: '10+0', label: '10 min', category: 'rapid', control: { kind: 'clock', initialMs: 600_000, incrementMs: 0 } },
  { id: 'unlimited', label: 'No limit', category: 'unlimited', control: { kind: 'unlimited' } },
]

export const DEFAULT_TIME_CONTROL_ID = '10+0'

export const findTimeControl = (id: string): TimeControlPreset | null =>
  TIME_CONTROLS.find((preset) => preset.id === id) ?? null

/**
 * The clock never ticks. It stores each side's remaining time and the instant
 * the side to move started thinking; everything else is arithmetic at read
 * time. That is what makes disconnection a non-event: nothing is running, so
 * nothing can be lost by going away.
 */
export interface ClockState {
  readonly whiteMs: number
  readonly blackMs: number
  /** Null before the game starts and once it has ended. */
  readonly turnStartedAt: Timestamp | null
}

export interface PlayerInfo {
  readonly name: string
  /** Whether this seat has ever connected. The clock waits for both. */
  readonly arrived: boolean
  /**
   * A signed-in player's picture. Null for guests, which is most people —
   * nothing in the room reducer knows or cares what an account is.
   */
  readonly avatar: string | null
}

export interface GameSlot {
  readonly index: number
  readonly game: Game
  readonly clock: ClockState
  readonly drawOfferedBy: PlayerSlot | null
}

export interface ChatMessage {
  readonly from: PlayerSlot | 'spectator'
  readonly name: string
  readonly text: string
  readonly at: Timestamp
}

export interface Reaction {
  readonly slot: PlayerSlot
  readonly emoji: string
  readonly at: Timestamp
}

export interface Score {
  readonly wins: readonly [number, number]
  readonly draws: number
}

/**
 * Who holds seat 1.
 *
 * A bot is not a special case anywhere in the reducer — it is a player whose
 * moves happen to arrive from the Durable Object instead of a WebSocket. That
 * is what makes clocks, rematch with colour swap, the running score and PGN
 * export all work against a bot with no extra code.
 */
export type Opponent =
  | { readonly kind: 'human' }
  | { readonly kind: 'ai'; readonly model: string }
  | { readonly kind: 'ai-vs-ai'; readonly models: readonly [string, string] }

export interface RoomState {
  readonly id: RoomId
  readonly timeControl: TimeControl
  readonly opponent: Opponent
  readonly players: readonly [PlayerInfo, PlayerInfo]
  /** Every game played in this room. The last one is live. */
  readonly games: readonly GameSlot[]
  readonly score: Score
  readonly chat: readonly ChatMessage[]
  readonly reactions: readonly Reaction[]
  readonly rematchOfferedBy: PlayerSlot | null
  readonly createdAt: Timestamp
}

export const currentGame = (state: RoomState): GameSlot => state.games[state.games.length - 1] as GameSlot

/** Seat 1 is the bot when there is exactly one. */
export const BOT_SLOT: PlayerSlot = 1

export const isAi = (opponent: Opponent): boolean => opponent.kind !== 'human'

/** The model driving `slot`, or null if that seat is a human. */
export const modelOfSlot = (opponent: Opponent, slot: PlayerSlot): string | null => {
  switch (opponent.kind) {
    case 'human':
      return null
    case 'ai':
      return slot === BOT_SLOT ? opponent.model : null
    case 'ai-vs-ai':
      return opponent.models[slot]
  }
}

export const botModelOf = (state: RoomState): string | null => modelOfSlot(state.opponent, BOT_SLOT)

/** Whose turn is it, as a seat rather than a colour? */
export const slotToMove = (state: RoomState): PlayerSlot => {
  const slot = currentGame(state)
  return slotOfColor(slot.game.position.turn, slot.index)
}

/** The six reactions the UI offers. Anything else is rejected. */
export const REACTIONS = ['🔥', '😂', '😱', '👏', '🤔', '😭'] as const
export type ReactionEmoji = (typeof REACTIONS)[number]

export const MAX_CHAT_LENGTH = 300
export const MAX_NAME_LENGTH = 20
/** Reactions are ephemeral in the UI; only the recent tail is worth keeping. */
export const REACTION_HISTORY = 20
